import {
  Calendar,
  CheckSquare,
  CreditCard,
  File,
  FileText,
  Home,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type SidebarIconProps = { className?: string }

function SidebarIcon({
  className,
  icon: Icon,
}: SidebarIconProps & { icon: LucideIcon }) {
  return (
    <Icon
      className={cn('size-5 shrink-0', className)}
      strokeWidth={1.8}
      aria-hidden
    />
  )
}

export function DashboardIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={Home} />
}

export function AgendaIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={Calendar} />
}

export function ClientsIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={Users} />
}

export function QuotesIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={FileText} />
}

export function InvoicesIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={CreditCard} />
}

export function PaymentsIcon({ className }: SidebarIconProps) {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={cn('size-5 shrink-0', className)}
      aria-hidden
    >
      <path
        d='M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M10 10C10 10 8.22236 10 7.08333 10M7.08333 10C6.59518 10 5.83333 10 5.83333 10M7.08333 10C7.08333 11.6667 8.38764 13.3333 10.2083 13.3333C11.2158 13.3333 11.9797 13.1106 12.5 12.6112M7.08333 10C7.08333 8.33333 8.125 6.66667 10.2083 6.66667C11.2158 6.66667 11.9797 6.8894 12.5 7.38873'
        stroke='currentColor'
        strokeWidth='1.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

export function ContractsIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={File} />
}

export function TasksIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={CheckSquare} />
}

export function TechniciansIcon({ className }: SidebarIconProps) {
  return <SidebarIcon className={className} icon={Wrench} />
}

export function SettingsIcon({ className }: SidebarIconProps) {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={cn('size-5 shrink-0', className)}
      aria-hidden
    >
      <g clipPath='url(#sidebar-settings-clip)'>
        <path
          d='M10 12.5C11.3807 12.5 12.5 11.3808 12.5 10C12.5 8.61933 11.3807 7.50004 10 7.50004C8.61929 7.50004 7.5 8.61933 7.5 10C7.5 11.3808 8.61929 12.5 10 12.5Z'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M16.1667 12.5C16.0557 12.7514 16.0226 13.0302 16.0717 13.3005C16.1207 13.5709 16.2496 13.8203 16.4417 14.0167L16.4917 14.0667C16.6466 14.2215 16.7696 14.4053 16.8534 14.6076C16.9373 14.81 16.9805 15.0268 16.9805 15.2459C16.9805 15.4649 16.9373 15.6818 16.8534 15.8841C16.7696 16.0864 16.6466 16.2703 16.4917 16.425C16.3369 16.58 16.1531 16.7029 15.9507 16.7868C15.7484 16.8707 15.5315 16.9139 15.3125 16.9139C15.0935 16.9139 14.8766 16.8707 14.6743 16.7868C14.4719 16.7029 14.2881 16.58 14.1333 16.425L14.0833 16.375C13.8869 16.1829 13.6375 16.0541 13.3672 16.005C13.0968 15.956 12.818 15.9891 12.5667 16.1C12.3202 16.2057 12.11 16.3811 11.9619 16.6047C11.8139 16.8282 11.7344 17.0902 11.7333 17.3584V17.5C11.7333 17.9421 11.5577 18.366 11.2452 18.6786C10.9326 18.9911 10.5087 19.1667 10.0667 19.1667C9.62464 19.1667 9.20072 18.9911 8.88816 18.6786C8.5756 18.366 8.4 17.9421 8.4 17.5V17.425C8.39355 17.1492 8.30427 16.8817 8.14376 16.6573C7.98325 16.4329 7.75895 16.2619 7.5 16.1667C7.24865 16.0558 6.96984 16.0227 6.69951 16.0717C6.42918 16.1207 6.17973 16.2496 5.98333 16.4417L5.93333 16.4917C5.77854 16.6467 5.59473 16.7696 5.3924 16.8535C5.19007 16.9373 4.97319 16.9805 4.75417 16.9805C4.53514 16.9805 4.31826 16.9373 4.11593 16.8535C3.9136 16.7696 3.72979 16.6467 3.575 16.4917C3.42004 16.3369 3.29711 16.1531 3.21323 15.9508C3.12936 15.7484 3.08619 15.5316 3.08619 15.3125C3.08619 15.0935 3.12936 14.8766 3.21323 14.6743C3.29711 14.472 3.42004 14.2882 3.575 14.1334L3.625 14.0834C3.81711 13.887 3.94599 13.6375 3.995 13.3672C4.04402 13.0969 4.01093 12.8181 3.9 12.5667C3.79436 12.3202 3.61896 12.11 3.39539 11.962C3.17181 11.8139 2.90982 11.7344 2.64167 11.7334H2.5C2.05797 11.7334 1.63405 11.5578 1.32149 11.2452C1.00893 10.9327 0.833333 10.5087 0.833333 10.0667C0.833333 9.62468 1.00893 9.20076 1.32149 8.8882C1.63405 8.57564 2.05797 8.40004 2.5 8.40004H2.575C2.85083 8.39359 3.11834 8.30431 3.34275 8.1438C3.56716 7.98329 3.7381 7.75899 3.83333 7.50004C3.94426 7.24869 3.97735 6.96988 3.92834 6.69955C3.87932 6.42922 3.75045 6.17977 3.55833 5.98337L3.50833 5.93337C3.35337 5.77859 3.23044 5.59477 3.14657 5.39244C3.06269 5.19011 3.01952 4.97323 3.01952 4.75421C3.01952 4.53518 3.06269 4.3183 3.14657 4.11597C3.23044 3.91364 3.35337 3.72983 3.50833 3.57504C3.66312 3.42008 3.84694 3.29715 4.04927 3.21327C4.2516 3.1294 4.46847 3.08623 4.6875 3.08623C4.90653 3.08623 5.1234 3.1294 5.32573 3.21327C5.52806 3.29715 5.71188 3.42008 5.86667 3.57504L5.91667 3.62504C6.11307 3.81715 6.36251 3.94603 6.63284 3.99504C6.90317 4.04406 7.18199 4.01097 7.43333 3.90004H7.5C7.74647 3.7944 7.95668 3.619 8.10474 3.39543C8.25281 3.17185 8.33226 2.90986 8.33333 2.64171V2.50004C8.33333 2.05801 8.50893 1.63409 8.82149 1.32153C9.13405 1.00897 9.55797 0.833374 10 0.833374C10.442 0.833374 10.866 1.00897 11.1785 1.32153C11.4911 1.63409 11.6667 2.05801 11.6667 2.50004V2.57504C11.6677 2.8432 11.7472 3.10519 11.8953 3.32876C12.0433 3.55234 12.2535 3.72774 12.5 3.83337C12.7513 3.9443 13.0302 3.97739 13.3005 3.92838C13.5708 3.87936 13.8203 3.75049 14.0167 3.55837L14.0667 3.50837C14.2215 3.35341 14.4053 3.23048 14.6076 3.14661C14.8099 3.06273 15.0268 3.01956 15.2458 3.01956C15.4649 3.01956 15.6817 3.06273 15.8841 3.14661C16.0864 3.23048 16.2702 3.35341 16.425 3.50837C16.58 3.66316 16.7029 3.84698 16.7868 4.04931C16.8706 4.25164 16.9138 4.46852 16.9138 4.68754C16.9138 4.90657 16.8706 5.12344 16.7868 5.32577C16.7029 5.5281 16.58 5.71192 16.425 5.86671L16.375 5.91671C16.1829 6.11311 16.054 6.36255 16.005 6.63288C15.956 6.90321 15.9891 7.18203 16.1 7.43337V7.50004C16.2056 7.74652 16.381 7.95672 16.6046 8.10478C16.8282 8.25285 17.0902 8.3323 17.3583 8.33337H17.5C17.942 8.33337 18.366 8.50897 18.6785 8.82153C18.9911 9.13409 19.1667 9.55801 19.1667 10C19.1667 10.4421 18.9911 10.866 18.6785 11.1786C18.366 11.4911 17.942 11.6667 17.5 11.6667H17.425C17.1568 11.6678 16.8949 11.7472 16.6713 11.8953C16.4477 12.0434 16.2723 12.2536 16.1667 12.5Z'
          stroke='currentColor'
          strokeWidth='1.6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <clipPath id='sidebar-settings-clip'>
          <rect width='20' height='20' fill='white' />
        </clipPath>
      </defs>
    </svg>
  )
}
